import * as React from "react";
import { Box, Checkbox, FormControlLabel, Typography, checkboxClasses } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { ZoneColors } from "./utils/zoneProcessor";

type ChartsWithMUIXProps = {
    width?: number;
    height?: number;
    xLabel?: string;
    yLabel?: string;
    visPanelWidth?: number;
    density?: "normal" | "compact";
    /**
     * Mobile layout: let the chart fill its container instead of locking it to
     * `width` (450px by default, which overflows a phone), and move the
     * visibility checkboxes underneath the plot instead of beside it.
     */
    fillWidth?: boolean;
    chartData: any;
    chartTimesData: number[];
};

function ChartsWithMUIX({
    width = 450,
    height = 250,
    xLabel,
    yLabel,
    visPanelWidth,
    density = "normal",
    fillWidth = false,
    chartData = {},
    chartTimesData = [],
}: ChartsWithMUIXProps) {
    const isCompact = density === "compact";
    const panelW = visPanelWidth ?? (isCompact ? 65 : 110);

    const [frameTimes, setFrameTimes] = React.useState<number[]>([111]);
    // const yNumbers = [0, 50, 100, 150, 200, 250, 300, 350];

    const [visValues, setVisValues] = React.useState<Record<string, boolean>>({});
    const [valueData, setValueData] = React.useState<any[]>([{ data: [1111] }]);

    React.useEffect(() => {
        if (chartTimesData)
            setFrameTimes(chartTimesData);
    }, [chartTimesData]);

    React.useEffect(() => {
        if (Object.keys(chartData).length === 0) {
            setValueData([]);
            setVisValues({});
            return;
        }

        const initialVisValues: Record<string, boolean> = {};

        const seriesData = Object.entries(chartData).map(([key, data]) => {
            initialVisValues[key] = visValues[key] !== undefined ? visValues[key] : true;

            const color = ZoneColors[key as keyof typeof ZoneColors];

            return {
                id: key.toLowerCase(),
                label: key,
                data,
                color: color,
                showMark: false,
                originalKey: key
            };
        });

        setValueData(seriesData);
        setVisValues(initialVisValues);
    }, [chartData]);

    React.useEffect(() => {
        if (Object.keys(chartData).length > 0) {
            setValueData(() => {
                const s: any[] = [];
                let clrIdx = 0;
                Object.entries(chartData).forEach(([key, data]) => {
                    if (visValues[key])
                        s.push({
                            id: key.toLowerCase(),
                            label: key,
                            data: Array.isArray(data) ? data : [],
                            color: ZoneColors[clrIdx],
                            showMark: false
                        });
                    clrIdx++;
                });
                return s;
            });
        }
        setVisValues(v => ({ ...v }));
    }, [visValues])

    return (
        <Box sx={{
            display: "flex",
            flexDirection: fillWidth ? 'column' : 'row',
            width: '100%',
            minWidth: 0,
            marginTop: isCompact ? 0 : '-10px',
        }}>
            <Box sx={{
                flex: 1,
                "& .MuiChartsLegend-root": { gap: '4px' },
                "& .MuiChartsAxis-label": {
                    transform: 'translateX(15px)',
                    fill: !isCompact ? 'var(--text-color)' : 'black'
                },
                "& .MuiChartsAxis-tickLabel ": { fill: !isCompact ? 'var(--text-color)' : 'black' },
                "& .MuiChartsAxis-bottom .MuiChartsAxis-line, & .MuiChartsAxis-bottom .MuiChartsAxis-tick": {
                    stroke: !isCompact ? 'var(--text-color)' : 'black'
                },
                border: isCompact ? '1px solid #999999ff' : 'none',
                borderRadius: '10px'
            }}>
                <LineChart
                    xAxis={[{
                        data: frameTimes,
                        label: xLabel ?? "Time (ms)",
                        scaleType: "linear",
                    }]}
                    yAxis={[
                        { tickNumber: 5, label: yLabel, position: isCompact ? 'left' : 'none' }
                    ]}
                    // yAxis={[
                    //     isCompact
                    //         ? { min: 0, max: 300, tickInterval: yNumbers, label: yLabel }
                    //         : { min: 0, max: 300, tickNumber: 100, label: yLabel },
                    // ]}
                    series={valueData}
                    height={height}
                    {...(fillWidth ? {} : { width })}
                    grid={{ horizontal: true }}
                    margin={{ left: isCompact ? 0 : 10, bottom: 10, right: 20, top: isCompact ? 20 : 10 }}
                    slotProps={{
                        noDataOverlay: { message: 'Loading...' },
                        legend: {
                            direction: 'vertical', position: { vertical: 'middle', horizontal: 'center' },
                            sx: {
                                visibility: isCompact ? 'visible' : 'hidden',
                                display: isCompact ? 'flex' : 'none',
                                p: 0, m: 0, alignItems: 'flex-start',
                                // gap: 2,     // itemGap: 2,
                                '& .MuiChartsLegend-label': {   // labelStyle: { fontSize: 22, lineHeight: 0.5, whiteSpace: 'nowrap' }
                                    fontSize: 12,
                                    // lineHeight: 0.5,
                                    // whiteSpace: 'nowrap',
                                },
                                '& .MuiChartsLegend-mark': {        // itemMarkWidth: 10,
                                    width: '10px',
                                },
                            },
                            // markGap: 4,
                            // itemGap: 2,
                            // itemMarkWidth: 10,
                            // labelStyle: { fontSize: 22, lineHeight: 0.5, whiteSpace: 'nowrap' }
                        }
                    }}
                />
            </Box>
            {!isCompact && (
                <Box sx={fillWidth
                    ? { width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0 }
                    : { width: panelW }}>
                    <Typography variant="subtitle2" sx={{ ml: 0.2, mb: fillWidth ? 0 : 1, fontSize: isCompact ? 10 : 12, color: "#3498db", width: fillWidth ? '100%' : undefined }}>Visibility</Typography>
                    <>
                        {Object.keys(chartData).map((key: string, idx: number) => {
                            return (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(visValues[key])}
                                            onChange={(e) =>
                                                setVisValues(v => ({
                                                    ...v,
                                                    [key]: e.target.checked
                                                }))
                                            }
                                            sx={{
                                                ml: 0,
                                                color: ZoneColors[idx],
                                                [`&.${checkboxClasses.checked}`]: { color: ZoneColors[idx] },
                                                '&:hover': { bgcolor: 'transparent' },
                                            }}
                                        />
                                    }
                                    label={key}
                                    sx={fillWidth ? { m: 0, '& .MuiFormControlLabel-label': { fontSize: 11 } } : undefined}
                                />
                            );
                        })}
                    </>
                </Box>
            )}
        </Box>
    );

}

export default React.memo(ChartsWithMUIX)
